/**
 * TherapistThreadList Component
 *
 * Sidebar listing therapist chat threads with create/delete capabilities.
 * Displays threads ordered by updated_at descending, with title, partner badge,
 * and relative date. Supports thread selection, creation, and deletion with
 * confirmation dialog.
 */

import { Plus, Trash2, Loader2, MessageSquare } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  useTherapistThreads,
  useCreateTherapistThread,
  useDeleteTherapistThread,
} from "../../hooks/useTherapistChat";

interface TherapistThreadListProps {
  selectedThreadId: string | null;
  onSelectThread: (id: string) => void;
  onThreadDeleted?: (deletedThreadId: string) => void;
}

export function TherapistThreadList({
  selectedThreadId,
  onSelectThread,
  onThreadDeleted,
}: TherapistThreadListProps) {
  const { data, isLoading } = useTherapistThreads();
  const createThread = useCreateTherapistThread();
  const deleteThread = useDeleteTherapistThread();

  const threads = data?.threads ?? [];
  const sortedThreads = [...threads].sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  const handleCreateThread = async () => {
    const newThread = await createThread.mutateAsync({});
    onSelectThread(newThread.id);
  };

  const handleDeleteThread = async (threadId: string) => {
    await deleteThread.mutateAsync(threadId);
    onThreadDeleted?.(threadId);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b">
        <Button
          onClick={handleCreateThread}
          disabled={createThread.isPending}
          className="w-full"
          size="sm"
        >
          {createThread.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          New Thread
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : sortedThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-muted-foreground">
            <MessageSquare className="h-8 w-8 mb-2" />
            <p className="text-sm">No conversations yet</p>
          </div>
        ) : (
          <div className="py-1">
            {sortedThreads.map((thread) => (
              <div
                key={thread.id}
                role="button"
                tabIndex={0}
                className={`group flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors ${
                  selectedThreadId === thread.id ? "bg-accent" : ""
                }`}
                onClick={() => onSelectThread(thread.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onSelectThread(thread.id);
                  }
                }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {thread.title ?? "New conversation"}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {thread.partner_name && (
                      <Badge
                        variant="secondary"
                        className="text-xs px-1.5 py-0"
                      >
                        {thread.partner_name}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(thread.updated_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete this conversation and all
                        its messages. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteThread(thread.id)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
